import React, { useMemo } from 'react';

type BedroomMarketRow = {
  bedrooms?: number | string;
  median?: number | null;
  totalListings?: number | null;
};

type YieldRow = {
  bedrooms: number | string;
  rent: number;
  salePrice: number;
  yieldPct: number;
  confidenceListings: number;
};

const money = (value: number) => `$${Math.round(value).toLocaleString()}`;

const ratingFor = (row: YieldRow) => {
  if (row.confidenceListings > 0 && row.confidenceListings <= 2) {
    return { label: 'Low confidence', color: '#b45353', background: '#fdecec' };
  }
  if (row.yieldPct >= 8) return { label: 'High', color: '#157a6e', background: '#e5f5f1' };
  if (row.yieldPct >= 6) return { label: 'Moderate', color: '#a66b00', background: '#fff3d9' };
  return { label: 'Low', color: '#b43d4b', background: '#fde9ec' };
};

const GrossYieldBedroomCard: React.FC<{
  saleRows?: BedroomMarketRow[];
  rentalRows?: BedroomMarketRow[];
}> = ({ saleRows, rentalRows }) => {
  const data = useMemo(() => {
    const rentals = new Map(
      (rentalRows || []).map((row) => [String(row.bedrooms), row]),
    );

    return (saleRows || [])
      .map((saleRow): YieldRow | null => {
        const rentalRow = rentals.get(String(saleRow.bedrooms));
        const salePrice = Number(saleRow.median) || 0;
        const rent = Number(rentalRow?.median) || 0;
        if (saleRow.bedrooms == null || salePrice <= 0 || rent <= 0) return null;
        const saleListings = Number(saleRow.totalListings) || 0;
        const rentalListings = Number(rentalRow?.totalListings) || 0;
        const positiveListings = [saleListings, rentalListings].filter((value) => value > 0);
        return {
          bedrooms: saleRow.bedrooms,
          rent,
          salePrice,
          yieldPct: (rent * 12 * 100) / salePrice,
          confidenceListings: positiveListings.length ? Math.min(...positiveListings) : 0,
        };
      })
      .filter((row): row is YieldRow => row != null)
      .sort((a, b) => Number(a.bedrooms) - Number(b.bedrooms));
  }, [rentalRows, saleRows]);

  if (!data.length) return null;

  const maxYield = Math.max(...data.map((row) => row.yieldPct), 1);

  return (
    <div
      style={{
        border: '1px solid #e8eef3',
        borderRadius: 14,
        background: '#fff',
        boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: '#12858d',
          fontSize: 18,
          fontWeight: 800,
          marginBottom: 14,
        }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: '#dff4f5',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
          }}
        >
          ↗
        </span>
        Estimated Gross Yield by Bedrooms
      </div>

      <div style={{ fontSize: 15, fontWeight: 700, color: '#17233d' }}>
        Estimated Gross Yield (by number of bedrooms)
      </div>
      <div style={{ color: '#64748b', fontSize: 12, margin: '3px 0 14px' }}>
        Gross Yield = Annual Median Rent ÷ Median Sale Price ⓘ
      </div>

      <div style={{ overflowX: 'auto', paddingBottom: 6, WebkitOverflowScrolling: 'touch' }}>
        <div style={{ minWidth: 650 }}>
          {data.map((row, index) => {
            const rating = ratingFor(row);
            return (
              <div
                key={String(row.bedrooms)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '96px minmax(260px, 1fr) 92px 112px',
                  gap: 12,
                  alignItems: 'center',
                  minHeight: 82,
                  borderTop: index > 0 ? '1px solid #edf0f2' : undefined,
                }}
              >
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#17233d' }}>
                    {row.bedrooms} {Number(row.bedrooms) === 1 ? 'bed' : 'beds'}
                  </div>
                  <div style={{ color: '#168f9a', fontSize: 25, lineHeight: 1, marginTop: 5 }}>▱</div>
                </div>

                <div>
                  <div style={{ height: 30, position: 'relative' }}>
                    <div
                      style={{
                        width: `${Math.max((row.yieldPct / maxYield) * 100, 4)}%`,
                        height: 30,
                        borderRadius: 3,
                        background:
                          rating.label === 'High'
                            ? 'linear-gradient(90deg, #1498a0, #0fa5ad)'
                            : rating.label === 'Moderate'
                              ? 'linear-gradient(90deg, #2581d8, #2175ca)'
                              : 'linear-gradient(90deg, #dbeafb, #cfe2f8)',
                      }}
                    />
                  </div>
                  <div style={{ marginTop: 5, fontSize: 11, color: '#475569' }}>
                    {money(row.rent)} rent / {money(row.salePrice)} sale price
                  </div>
                </div>

                <div style={{ fontSize: 24, fontWeight: 800, color: '#17233d', textAlign: 'right' }}>
                  {row.yieldPct.toFixed(1)}%
                </div>

                <div
                  style={{
                    justifySelf: 'center',
                    minWidth: 88,
                    borderRadius: 8,
                    background: rating.background,
                    color: rating.color,
                    padding: '7px 8px',
                    textAlign: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {rating.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GrossYieldBedroomCard;
